import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Waypoints, ArrowLeft, Mail, ShieldCheck, Loader2, FlaskConical } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { getAppConfig } from "@/lib/config";
import { useAuth } from "@/context/AuthContext";

export default function AuthPage() {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const testMode = searchParams.get("test") === "1";

  useEffect(() => {
    let active = true;
    getAppConfig().then((c) => {
      if (!active) return;
      if (!c.direct_login_enabled && !testMode) {
        window.location.replace(c.main_app_url);
        return;
      }
      setCfg(c);
    });
    return () => { active = false; };
  }, [testMode]);

  const requestOtp = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-otp", { email });
      setStep("otp");
      if (data.dev_otp) {
        toast.info(`Dev code: ${data.dev_otp}`, { description: "Email provider not configured — using this code." });
      } else {
        toast.success("Verification code sent to your email.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (value) => {
    const otp = value ?? code;
    if (otp.length !== 6) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, code: otp, name: name || undefined });
      await login(data.token, data.user);
      toast.success("Signed in successfully.");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  if (!cfg) {
    return (
      <div className="min-h-screen bg-[#F5F6F8] mesh-grid flex items-center justify-center" data-testid="auth-loading">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6F8] mesh-grid flex items-center justify-center px-4">
      <div className="absolute top-6 left-6">
        <Link to="/" className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors text-sm">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="flex items-center justify-center mb-8">
          <img src="/logo-jbn.png" alt="JobBasket Network" className="h-10 w-auto object-contain" />
        </div>

        <div className="glass rounded-2xl p-7 sm:p-8">
          {testMode && !cfg.direct_login_enabled && (
            <div data-testid="test-login-badge" className="mb-5 flex items-center justify-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 font-medium">
              <FlaskConical className="w-3.5 h-3.5" /> Test login — preview only. Real users sign in from the main app.
            </div>
          )}
          {step === "email" ? (
            <>
              <h1 className="text-2xl font-bold font-display">Sign in to continue</h1>
              <p className="text-sm text-slate-500 mt-2">
                Enter your email and we'll send you a one-time code. New here? An account is created automatically.
              </p>
              <form onSubmit={requestOtp} className="mt-6 space-y-4">
                <div>
                  <Label className="text-slate-700 text-sm">Full name <span className="text-slate-400">(optional)</span></Label>
                  <Input
                    data-testid="auth-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ada Lovelace"
                    className="mt-1.5 bg-slate-50 border-slate-300 text-slate-900 focus-visible:ring-amber-500/40 h-11"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 text-sm">Email address</Label>
                  <div className="relative mt-1.5">
                    <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      data-testid="auth-email-input"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-9 bg-slate-50 border-slate-300 text-slate-900 focus-visible:ring-amber-500/40 h-11"
                    />
                  </div>
                </div>
                <Button
                  data-testid="request-otp-button"
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-amber-500 text-black hover:bg-amber-400 font-semibold transition-colors"
                >
                  {loading ? "Sending…" : "Send verification code"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold font-display">Enter your code</h1>
              <p className="text-sm text-slate-500 mt-2">
                We sent a 6-digit code to <span className="text-amber-400">{email}</span>.
              </p>
              <div className="mt-7 flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={code}
                  onChange={(v) => {
                    setCode(v);
                    if (v.length === 6) verifyOtp(v);
                  }}
                  data-testid="otp-input"
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot
                        key={i}
                        index={i}
                        className="w-11 h-12 text-lg bg-slate-50 border-slate-300 text-amber-400"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button
                data-testid="otp-submit-button"
                onClick={() => verifyOtp()}
                disabled={loading || code.length !== 6}
                className="w-full h-11 mt-7 bg-amber-500 text-black hover:bg-amber-400 font-semibold transition-colors"
              >
                {loading ? "Verifying…" : "Verify & continue"}
              </Button>
              <button
                onClick={() => { setStep("email"); setCode(""); }}
                className="w-full text-center text-sm text-slate-500 hover:text-slate-800 mt-4 transition-colors"
                data-testid="change-email-button"
              >
                Use a different email
              </button>
            </>
          )}
        </div>
        <p className="flex items-center justify-center gap-1.5 text-xs text-slate-500 mt-5">
          <ShieldCheck className="w-3.5 h-3.5" /> Secured with one-time passcodes
        </p>
      </motion.div>
    </div>
  );
}
